/**
 * Critical layout rules inlined into HTML sent to the PDF export service.
 *
 * @param {{ pageMargin?: string }} [options]
 * - pageMargin: CSS @page margin (e.g. `120px 0 60px 0`). Use when Puppeteer
 *   displayHeaderFooter is on — `@page { margin: 0 }` makes content paint under
 *   headers/footers on every page. Omit for full-bleed Plan/Policy sheets.
 */
export default function getReportPdfExportStyles({ pageMargin } = {}) {
  const pageMarginRule = pageMargin ? `margin: ${pageMargin};` : 'margin: 0;';
  // When header/footer margins shrink the page box, a full 29.7cm back cover overflows
  // onto a second page. Fit the sheet to the printable area instead.
  const marginParts = String(pageMargin || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const marginTop = marginParts[0] || '0';
  const marginBottom = marginParts[2] || marginParts[0] || '0';
  const backCoverPageRules = pageMargin
    ? `
.risk-report-preview > section.risk-report-preview-back-cover-page {
  height: calc(29.7cm - ${marginTop} - ${marginBottom}) !important;
  max-height: calc(29.7cm - ${marginTop} - ${marginBottom}) !important;
  page-break-before: always;
  page-break-after: auto;
  page-break-inside: avoid !important;
  break-inside: avoid !important;
  overflow: hidden !important;
  background: #fff !important;
}

.risk-report-preview-back-cover {
  height: 100% !important;
  background: #fff !important;
}

.risk-report-preview-back-cover .company-intro {
  bottom: 420px !important;
}

.risk-report-preview-back-cover .company-info {
  bottom: 220px !important;
  height: 180px !important;
}
`
    : '';

  return `
@page {
  size: A4 portrait;
  ${pageMarginRule}
}

html,
body,
body.partner-aviva {
  margin: 0 !important;
  padding: 0 !important;
  /* Partner app chrome grey must not show on under-filled last pages */
  background: #fff !important;
  background-color: #fff !important;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.plan-preview,
.policy-preview,
.risk-report-preview,
.risk-report-preview-main,
.risk-report-preview > section,
.plan-preview > section,
.policy-preview > section {
  margin: 0;
  padding: 0;
  background: #fff !important;
  background-color: #fff !important;
}

.plan-preview > section:not(.dynamic),
.policy-preview > section,
.risk-report-preview > section {
  overflow: hidden;
  page-break-after: always;
  break-after: page;
  page-break-inside: avoid;
  break-inside: avoid;
  box-sizing: border-box;
}

.plan-preview-page:not(.dynamic),
.policy-preview-page {
  position: relative;
  overflow: hidden !important;
  page-break-after: always;
  break-after: page;
  page-break-inside: avoid;
  break-inside: avoid;
  height: 29.7cm;
  max-height: 29.7cm;
  box-sizing: border-box;
  background: #fff !important;
}

.plan-preview-page:not(.dynamic) > main,
.policy-preview-page > main {
  overflow: hidden !important;
  background: #fff !important;
}

.plan-preview-page:not(.dynamic) > footer,
.policy-preview-page > footer {
  z-index: 3;
  background-color: #fff;
}

/* Plan A.3 activation cards — force white faces in PDF (no z-index:-1 overlays). */
.plan-preview .invocation-level-card {
  background-color: #fff !important;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.plan-preview .invocation-level-card .invocation-level-title {
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

/* Kill Aviva/app .content chrome grey inside report sheets */
.risk-report-preview .content,
.risk-report-preview-main .content,
.policy-preview .content,
.plan-preview .content,
.plan-preview-page .content,
.policy-preview-page .content {
  background: transparent !important;
  background-color: transparent !important;
}

.risk-report-preview-main .content {
  padding-bottom: 0;
}

/* Match master Risk print breaks for flowing body + tables */
.risk-report-preview-main {
  page-break-after: always;
}

.risk-report-preview-main .title {
  page-break-before: always;
}

.risk-report-preview-main p,
.risk-report-preview-main li,
.risk-report-preview-main thead,
.risk-report-preview-main .title,
.risk-report-preview-main .widget {
  page-break-inside: avoid;
}

.risk-report-preview-main h1,
.risk-report-preview-main h2,
.risk-report-preview-main h3 {
  page-break-after: avoid;
}

${backCoverPageRules}
`;
}
